import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Checkbox,
  FormControl,
  FormLabel,
  Button,
  Chip,
  alpha,
  InputAdornment
} from '@mui/material';
import {
  List as ListIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { getPrioridadeColor } from '../../utils/themeHelper';

/**
 * Componente de formulário para serviços atendidos pelo usuário
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.formData - Dados do formulário
 * @param {Array} props.servicosDisponiveis - Lista de serviços disponíveis
 * @param {string} props.searchServico - Termo de busca para serviços
 * @param {Function} props.setSearchServico - Função para atualizar o termo de busca
 * @param {Function} props.handleToggleServico - Função para alternar serviço
 * @param {Function} props.handlePrioridadeChange - Função para alterar prioridade
 * @param {boolean} props.carregandoServicos - Flag indicando se serviços estão carregando
 * @param {string|null} props.erroServicos - Mensagem de erro ou null
 * @param {Function} props.carregarServicos - Função para recarregar serviços
 * @param {Object} props.customTheme - Tema personalizado
 * @returns {JSX.Element}
 */
const ServicosAtendidosForm = ({
  formData,
  servicosDisponiveis,
  searchServico,
  setSearchServico,
  handleToggleServico,
  handlePrioridadeChange,
  carregandoServicos,
  erroServicos,
  carregarServicos,
  customTheme
}) => {
  // Função para filtrar serviços com base na pesquisa
  const filteredServicos = React.useMemo(() => {
    return servicosDisponiveis.filter(
      servico => servico.nome.toLowerCase().includes(searchServico.toLowerCase()) || 
                servico.categoria?.toLowerCase().includes(searchServico.toLowerCase())
    );
  }, [servicosDisponiveis, searchServico]);

  // Função para limpar a pesquisa de serviços
  const handleClearSearch = () => {
    setSearchServico('');
  };

  // Função para atualizar o valor da pesquisa de serviços
  const handleSearchChange = (e) => {
    setSearchServico(e.target.value);
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography 
        variant="h6" 
        component="h2" 
        sx={{ mb: 3, display: 'flex', alignItems: 'center' }}
      >
        <ListIcon sx={{ mr: 1 }} />
        Serviços Atendidos
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
        {/* Coluna da Esquerda - Pesquisar e Adicionar Serviços */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              mb: 2, 
              borderRadius: '12px',
              border: '1px solid',
              borderColor: alpha(customTheme.palette.primary.main, 0.15),
              backgroundColor: theme => theme.palette.mode === 'dark' 
                ? alpha(customTheme.palette.background.paper, 0.6)
                : alpha(customTheme.palette.background.paper, 0.8)
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Selecionar Serviços
            </Typography>
            
            {/* Campo de Pesquisa */}
            <Box sx={{ position: 'relative', mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Pesquisar serviços..."
                value={searchServico}
                onChange={handleSearchChange}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchServico && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClearSearch}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: '10px',
                    backgroundColor: theme => theme.palette.mode === 'dark' 
                      ? alpha(customTheme.palette.common.white, 0.05)
                      : alpha(customTheme.palette.common.black, 0.03),
                    '&:hover': {
                      backgroundColor: theme => theme.palette.mode === 'dark' 
                        ? alpha(customTheme.palette.common.white, 0.08)
                        : alpha(customTheme.palette.common.black, 0.05),
                    }
                  }
                }}
              />
            </Box>
            
            {/* Lista de Serviços Disponíveis */}
            <Box 
              sx={{ 
                maxHeight: '350px', 
                overflowY: 'auto',
                pr: 1,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme => alpha(theme.palette.primary.main, 0.2),
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: theme => alpha(theme.palette.primary.main, 0.4),
                }
              }}
            >
              {carregandoServicos && (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  color: 'text.secondary',
                  backgroundColor: theme => alpha(theme.palette.background.default, 0.6),
                  borderRadius: '8px'
                }}>
                  <Typography variant="body2">
                    Carregando serviços...
                  </Typography>
                </Box>
              )}
              
              {erroServicos && !carregandoServicos && (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  color: 'error.main',
                  backgroundColor: theme => alpha(theme.palette.error.light, 0.1),
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: alpha(customTheme.palette.error.main, 0.2)
                }}>
                  <Typography variant="body2" mb={1}>
                    Erro ao carregar serviços
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {erroServicos}
                  </Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    color="primary" 
                    onClick={carregarServicos}
                    sx={{ mt: 2, borderRadius: '8px', textTransform: 'none' }}
                  >
                    Tentar novamente
                  </Button>
                </Box>
              )}
              
              {!carregandoServicos && !erroServicos && filteredServicos.length === 0 && (
                <Box sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  color: 'text.secondary',
                  backgroundColor: theme => alpha(theme.palette.background.default, 0.6),
                  borderRadius: '8px'
                }}>
                  <Typography variant="body2">
                    {searchServico 
                      ? 'Nenhum serviço encontrado com o termo pesquisado.' 
                      : 'Nenhum serviço disponível no momento.'}
                  </Typography>
                </Box>
              )}
              
              {!carregandoServicos && !erroServicos && filteredServicos.length > 0 && (
                filteredServicos.map((servico) => {
                  const isSelected = formData.servicos_atendidos.atende.includes(servico.id);
                  return (
                    <Box
                      key={servico.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        mb: 1,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        backgroundColor: isSelected 
                          ? alpha(customTheme.palette.primary.main, 0.08)
                          : 'transparent',
                        borderLeft: '3px solid',
                        borderColor: isSelected 
                          ? customTheme.palette.primary.main
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? alpha(customTheme.palette.primary.main, 0.12)
                            : alpha(customTheme.palette.action.hover, 0.1),
                        }
                      }}
                      onClick={() => handleToggleServico(servico.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Checkbox 
                          checked={isSelected}
                          color="primary"
                          sx={{ p: 0.5, mr: 1 }}
                        />
                        <Box>
                          <Typography variant="body1" fontWeight={500}>
                            {servico.nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {servico.categoria || servico.abreviacao || ''}
                          </Typography>
                        </Box>
                      </Box>
                      {isSelected && (
                        <Chip 
                          label="Adicionado" 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem', 
                            height: 24,
                            opacity: 0.8
                          }}
                        />
                      )}
                    </Box>
                  );
                })
              )}
            </Box>
          </Paper>
        </Box>
        
        {/* Coluna da Direita - Serviços Adicionados e Configuração de Prioridade */}
        <Box sx={{ flex: 1, width: '100%' }}>
          <Paper
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: '12px',
              border: '1px solid',
              borderColor: alpha(customTheme.palette.primary.main, 0.15),
              backgroundColor: theme => alpha(theme.palette.background.paper, 0.8)
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Serviços Adicionados ({formData.servicos_atendidos.atende.length})
            </Typography>
            
            {formData.servicos_atendidos.atende.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                color: 'text.secondary',
                backgroundColor: theme => alpha(theme.palette.background.default, 0.6),
                borderRadius: '8px',
                border: '1px dashed',
                borderColor: 'divider'
              }}>
                <Typography variant="body2" mb={1}>
                  Nenhum serviço adicionado.
                </Typography>
                <Typography variant="caption">
                  Selecione serviços na lista à esquerda para adicionar.
                </Typography>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  maxHeight: '430px', 
                  overflowY: 'auto',
                  pr: 1,
                  scrollbarWidth: 'thin',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: theme => alpha(theme.palette.primary.main, 0.2),
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: theme => alpha(theme.palette.primary.main, 0.4),
                  }
                }}
              >
                {servicosDisponiveis
                  .filter(servico => formData.servicos_atendidos.atende.includes(servico.id))
                  .map((servico) => {
                    const prioridade = formData.servicos_atendidos.prioridades[servico.id] || 'normal';
                    
                    return (
                      <Box
                        key={servico.id}
                        sx={{
                          p: 2,
                          mb: 2,
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: alpha(customTheme.palette.primary.main, 0.2),
                          backgroundColor: alpha(customTheme.palette.background.paper, 0.5)
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mb: 1.5
                        }}>
                          <Box>
                            <Typography variant="body1" fontWeight={500}>
                              {servico.nome}
                            </Typography>
                            <Chip 
                              label={servico.categoria || servico.abreviacao || ''} 
                              size="small" 
                              sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }}
                            />
                          </Box>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleToggleServico(servico.id)}
                            sx={{ 
                              backgroundColor: alpha(customTheme.palette.error.main, 0.1),
                              '&:hover': {
                                backgroundColor: alpha(customTheme.palette.error.main, 0.2),
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <FormControl size="small" fullWidth>
                          <FormLabel 
                            component="legend" 
                            sx={{ 
                              fontSize: '0.75rem', 
                              mb: 0.5,
                              color: 'text.secondary'
                            }}
                          >
                            Nível de Prioridade
                          </FormLabel>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {['baixa', 'normal', 'alta', 'urgente'].map((nivel) => (
                              <Button
                                key={nivel}
                                size="small"
                                variant={prioridade === nivel ? "contained" : "outlined"}
                                onClick={() => handlePrioridadeChange(servico.id, nivel)}
                                sx={{
                                  flex: 1,
                                  py: 0.5,
                                  textTransform: 'capitalize',
                                  fontSize: '0.75rem',
                                  bgcolor: prioridade === nivel 
                                    ? getPrioridadeColor(nivel, 'background', customTheme) 
                                    : 'transparent',
                                  color: prioridade === nivel 
                                    ? getPrioridadeColor(nivel, 'text', customTheme) 
                                    : getPrioridadeColor(nivel, 'border', customTheme),
                                  borderColor: getPrioridadeColor(nivel, 'border', customTheme),
                                  '&:hover': {
                                    bgcolor: prioridade === nivel 
                                      ? getPrioridadeColor(nivel, 'backgroundHover', customTheme) 
                                      : alpha(getPrioridadeColor(nivel, 'border', customTheme), 0.1),
                                  },
                                }}
                              >
                                {nivel.charAt(0).toUpperCase() + nivel.slice(1)}
                              </Button>
                            ))}
                          </Box>
                        </FormControl>
                      </Box>
                    );
                  })
                }
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicosAtendidosForm; 